import React, { useState, useEffect } from 'react';
import { Plus, Eye, Edit, Trash2, Search, Upload, X } from 'lucide-react';
import { BASE_URL, newsAPI, newsCategoriesAPI } from '../services/api';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const News = () => {
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedNews, setSelectedNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    shortDescription: '',
    longDescription: '',
    newsDate: '',
    category: '',
    image: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchNews();
    fetchCategories();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await newsAPI.getAll();
      if (response.data.success) {
        setNews(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await newsCategoriesAPI.getAll();
      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleAdd = () => {
    setModalType('add');
    setFormData({
      title: '',
      shortDescription: '',
      longDescription: '',
      newsDate: '',
      category: '',
      image: null
    });
    setImagePreview(null);
    setIsModalOpen(true);
  };

  const handleEdit = (newsItem) => {
    setModalType('edit');
    setSelectedNews(newsItem);
    setFormData({
      title: newsItem.title,
      shortDescription: newsItem.shortDescription,
      longDescription: newsItem.longDescription,
      newsDate: newsItem.newsDate,
      category: newsItem.category._id,
      image: null
    });
    
    setImagePreview(newsItem.imageUrl ? `${BASE_URL}{newsItem.imageUrl}` : null);
    setIsModalOpen(true);
  };

  const handleView = (newsItem) => {
    setModalType('view');
    setSelectedNews(newsItem);
    setIsModalOpen(true);
  };

  const handleDelete = async (newsItem) => {
    if (window.confirm(`Are you sure you want to delete "${newsItem.title}"?`)) {
      try {
        const response = await newsAPI.delete(newsItem._id);
        if (response.data.success) {
          toast.success('News deleted successfully');
          fetchNews();
        }
      } catch (error) {
        console.error('Error deleting news:', error);
      }
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, image: null });
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.shortDescription.trim() || 
        !formData.longDescription.trim() || !formData.newsDate.trim() || !formData.category) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('shortDescription', formData.shortDescription);
      submitData.append('longDescription', formData.longDescription);
      submitData.append('newsDate', formData.newsDate);
      submitData.append('category', formData.category);
      
      if (formData.image) {
        submitData.append('image', formData.image);
      }

      let response;
      if (modalType === 'add') {
        response = await newsAPI.create(submitData);
      } else if (modalType === 'edit') {
        response = await newsAPI.update(selectedNews._id, submitData);
      }

      if (response.data.success) {
        toast.success(`News ${modalType === 'add' ? 'created' : 'updated'} successfully`);
        setIsModalOpen(false);
        fetchNews();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredNews = news.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.categoryName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">General News</h1>
        <button onClick={handleAdd} className="bg-gradient-to-r from-blue-500 to-purple-600 btn-primary">
          <Plus className="h-5 w-5" />
          Add News
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search news..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* News Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredNews.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500">No news found</p>
            </div>
          ) : (
            filteredNews.map((newsItem) => (
              <div key={newsItem._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {newsItem.imageUrl && (
                  <img
                    // src={`http://localhost:5900${newsItem.imageUrl}`}
                    src={`${BASE_URL}${newsItem.imageUrl}`}
                    // src={newsItem.imageUrl}
                    alt={newsItem.title}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {newsItem.category.categoryName}
                    </span>
                    <span className="text-xs text-gray-500">{newsItem.newsDate}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {newsItem.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                    {newsItem.shortDescription}
                  </p>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => handleView(newsItem)}
                      className="text-blue-600 hover:text-blue-900 p-2 rounded-full hover:bg-blue-50"
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(newsItem)}
                      className="text-yellow-600 hover:text-yellow-900 p-2 rounded-full hover:bg-yellow-50"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(newsItem)}
                      className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          modalType === 'add' ? 'Add News' :
          modalType === 'edit' ? 'Edit News' : 'View News'
        }
        size="lg"
      >
        {modalType === 'view' ? (
          <div className="space-y-6">
            {selectedNews?.imageUrl && (
              <img
                // src={`http://localhost:5900${selectedNews.imageUrl}`}
                src={`${BASE_URL}${selectedNews.imageUrl}`}
                // src={selectedNews.imageUrl}
                alt={selectedNews.title}
                className="w-full h-64 object-cover rounded-lg"
              />
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
              <p className="text-lg text-gray-900">{selectedNews?.title}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {selectedNews?.category.categoryName}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
              <p className="text-sm text-gray-600">{selectedNews?.newsDate}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Short Description</label>
              <p className="text-gray-900">{selectedNews?.shortDescription}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Long Description</label>
              <p className="text-gray-900 whitespace-pre-wrap">{selectedNews?.longDescription}</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="input-field"
                placeholder="Enter news title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="input-field"
                required
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.categoryName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                News Date *
              </label>
              <input
                type="text"
                value={formData.newsDate}
                onChange={(e) => setFormData({ ...formData, newsDate: e.target.value })}
                className="input-field"
                placeholder="e.g., 28 जून, 2017"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Short Description *
              </label>
              <textarea
                value={formData.shortDescription}
                onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                className="textarea-field"
                rows="3"
                placeholder="Enter short description"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Long Description *
              </label>
              <textarea
                value={formData.longDescription}
                onChange={(e) => setFormData({ ...formData, longDescription: e.target.value })}
                className="textarea-field"
                rows="6"
                placeholder="Enter detailed description"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image (Max 5MB)
              </label>
              <div className="space-y-4">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="mt-4">
                      <label className="cursor-pointer">
                        <span className="mt-2 block text-sm font-medium text-gray-900">
                          Upload an image
                        </span>
                        <input
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="btn-secondary"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-gradient-to-r from-blue-500 to-purple-600 btn-primary"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    {modalType === 'add' ? 'Creating...' : 'Updating...'}
                  </>
                ) : (
                  modalType === 'add' ? 'Create News' : 'Update News'
                )}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default News;